import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { homeFor } from "@/App";
import AuthShell, { inputCls, labelCls, primaryBtn, errText } from "@/components/AuthShell";

export function ForgotPassword() {
  const [params] = useSearchParams();
  const area = params.get("area") || "customer";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(errText(err, "Request failed")); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell kicker="Account recovery" title="Reset your password" sub="Enter the email you sign in with. We will send a one-hour reset link." testid="forgot-page">
      {sent ? (
        <div data-testid="forgot-sent" className="border border-cyan-400/30 bg-cyan-400/[0.04] p-4 text-sm text-zinc-300">If an account exists for <span className="font-mono text-cyan-300">{email}</span>, a reset link is on its way. Check spam if it does not arrive within a minute.</div>
      ) : (
        <form onSubmit={submit}>
          <label className={labelCls}>Email</label>
          <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mb-6`} required autoFocus />
          <button data-testid="forgot-submit" disabled={busy} className={primaryBtn}>{busy ? "SENDING…" : "SEND RESET LINK"} <ArrowRight size={14} /></button>
        </form>
      )}
      <Link to={`/${area}/login`} data-testid="forgot-back" className="block mt-6 text-xs text-zinc-400 hover:text-zinc-200">← Back to sign in</Link>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw.a });
      toast.success("Password updated — sign in with your new password");
      nav(`/${data.area}/login`);
    } catch (err) { toast.error(errText(err, "Reset failed")); }
    finally { setBusy(false); }
  };
  if (!token) return <AuthShell kicker="Account recovery" title="Invalid link" testid="reset-invalid"><p className="text-sm text-zinc-400">This reset link is missing its token. <Link to="/forgot-password" className="text-cyan-300 underline">Request a new one</Link>.</p></AuthShell>;
  return (
    <AuthShell kicker="Account recovery" title="Choose a new password" sub="Minimum 10 characters. All other sessions will be signed out." testid="reset-page">
      <form onSubmit={submit}>
        <label className={labelCls}>New password</label>
        <input data-testid="reset-password" type="password" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className={`${inputCls} mb-4`} required minLength={10} autoComplete="new-password" />
        <label className={labelCls}>Confirm password</label>
        <input data-testid="reset-confirm" type="password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className={`${inputCls} mb-6`} required minLength={10} autoComplete="new-password" />
        <button data-testid="reset-submit" disabled={busy} className={primaryBtn}>{busy ? "UPDATING…" : "UPDATE PASSWORD"} <ArrowRight size={14} /></button>
      </form>
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ status: "working" });
  useEffect(() => {
    if (!token) { setState({ status: "error", msg: "This verification link is missing its token." }); return; }
    api.post("/auth/verify-email", { token })
      .then(({ data }) => { setSession(data); setState({ status: "ok", user: data.user }); setTimeout(() => nav(homeFor(data.user)), 1800); })
      .catch((err) => setState({ status: "error", msg: errText(err, "Verification failed") }));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <AuthShell kicker="Email verification" title={state.status === "ok" ? "You're all set" : state.status === "error" ? "Link problem" : "Verifying…"} testid="verify-page">
      {state.status === "working" && <div data-testid="verify-working" className="text-sm text-zinc-400 font-mono">Confirming your email address…</div>}
      {state.status === "ok" && <div data-testid="verify-ok" className="border border-emerald-400/30 bg-emerald-400/[0.05] p-4 text-sm text-zinc-200 flex gap-3"><CheckCircle2 className="text-emerald-400 shrink-0" size={20} /><div>Email confirmed. Next: choose your plan on the Billing page to activate the workspace. A welcome email with installation steps is on its way.</div></div>}
      {state.status === "error" && (
        <div data-testid="verify-error" className="border border-red-400/30 bg-red-400/[0.05] p-4 text-sm text-zinc-200 flex gap-3"><XCircle className="text-red-400 shrink-0" size={20} /><div>{state.msg}<div className="mt-2 text-xs text-zinc-400">Sign in and use “Resend verification email” to get a fresh link.</div></div></div>
      )}
      <Link to="/customer/login" className="block mt-6 text-xs text-zinc-400 hover:text-zinc-200">Go to sign in →</Link>
    </AuthShell>
  );
}
